import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, PencilIcon, PlayIcon } from '@heroicons/react/24/outline';

const VideoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [video, setVideo] = useState(null);
  const [device, setDevice] = useState(null);
  const [trip, setTrip] = useState(null);
  const [event, setEvent] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  useEffect(() => {
    fetchVideo();
  }, [id]);

  const fetchVideo = async () => {
    const { data } = await get(`/api/videos/${id}`);
    if (data) {
      setVideo(data);
      
      // Fetch related data
      if (data.deviceId) {
        const deviceRes = await get(`/api/devices/${data.deviceId}`);
        if (deviceRes.data) setDevice(deviceRes.data);
      }
      if (data.tripId) {
        const tripRes = await get(`/api/trips/${data.tripId}`);
        if (tripRes.data) setTrip(tripRes.data);
      }
      if (data.eventId) {
        const eventRes = await get(`/api/events/${data.eventId}`);
        if (eventRes.data) setEvent(eventRes.data);
      }
      if (data.videoFileId) {
        const fileRes = await get(`/api/files/${data.videoFileId}`);
        if (fileRes.data) setVideoFile(fileRes.data);
      }
      if (data.thumbnailFileId) {
        const thumbRes = await get(`/api/files/${data.thumbnailFileId}`);
        if (thumbRes.data) setThumbnailFile(thumbRes.data);
      }
    }
  };

  const handlePlayVideo = () => {
    if (video?.videoFileId) {
      window.open(`/api/files/${video.videoFileId}`, '_blank');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) return <LoadingSpinner />;
  if (!video) return <div className="p-6">Video not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/videos/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Video Details</h1>
        </div>
        <div className="flex space-x-2">
          {video.videoFileId && (
            <button
              onClick={handlePlayVideo}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <PlayIcon className="h-5 w-5" />
              <span>Play</span>
            </button>
          )}
          <button
            onClick={() => navigate(`/videos/edit/${id}`)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500">ID</label>
            <p className="text-lg font-medium">{video.id}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">UUID</label>
            <p className="text-lg font-medium font-mono text-sm">{video.uuid || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Device</label>
            <p className="text-lg font-medium">
              {device ? (
                <button
                  onClick={() => navigate(`/devices/${device.id}`)}
                  className="text-indigo-600 hover:text-indigo-900"
                >
                  {device.brand} - {device.devType} (ID: {device.id})
                </button>
              ) : (
                `Device #${video.deviceId}`
              )}
            </p>
          </div>
          {video.tripId && (
            <div>
              <label className="text-sm text-gray-500">Trip</label>
              <p className="text-lg font-medium">
                {trip ? (
                  <button
                    onClick={() => navigate(`/trips/${trip.id}`)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Trip #{trip.id} - {trip.label || 'No label'}
                  </button>
                ) : (
                  `Trip #${video.tripId}`
                )}
              </p>
            </div>
          )}
          {video.eventId && (
            <div>
              <label className="text-sm text-gray-500">Event</label>
              <p className="text-lg font-medium">
                {event ? (
                  <button
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Event #{event.id} - {event.eventType?.description || 'Unknown'}
                  </button>
                ) : (
                  `Event #${video.eventId}`
                )}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm text-gray-500">Timestamp</label>
            <p className="text-lg font-medium">
              {video.timestamp ? new Date(video.timestamp).toLocaleString() : '-'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Duration</label>
            <p className="text-lg font-medium">{formatDuration(video.duration)}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Resolution</label>
            <p className="text-lg font-medium">{video.resolution || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Framerate</label>
            <p className="text-lg font-medium">{video.framerate ? `${video.framerate} fps` : '-'}</p>
          </div>
          {video.encryptionKeyId && (
            <div className="md:col-span-2">
              <label className="text-sm text-gray-500">Encryption Key ID</label>
              <p className="text-lg font-medium font-mono text-sm">{video.encryptionKeyId}</p>
            </div>
          )}
        </div>

        {/* Video Files Section */}
        {(videoFile || thumbnailFile) && (
          <div className="mt-8 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Associated Files</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videoFile && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="text-sm text-gray-500">Video File</label>
                  <p className="text-lg font-medium">{videoFile.path?.split('/').pop()}</p>
                  <button
                    onClick={() => window.open(videoFile.path, '_blank')}
                    className="mt-2 text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                  >
                    Download Video
                  </button>
                </div>
              )}
              {thumbnailFile && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="text-sm text-gray-500">Thumbnail File</label>
                  <p className="text-lg font-medium">{thumbnailFile.path?.split('/').pop()}</p>
                  <button
                    onClick={() => window.open(thumbnailFile.path, '_blank')}
                    className="mt-2 text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                  >
                    View Thumbnail
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Thumbnail Preview */}
        {thumbnailFile && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Thumbnail Preview</h3>
            <img
              src={thumbnailFile.path}
              alt="Video thumbnail"
              className="max-w-full rounded-lg shadow-lg"
              style={{ maxHeight: '300px' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoDetails;